import React, { useState, useRef, useEffect, useId } from 'react';
import classNames from 'classnames';
import { PlacesType } from 'react-tooltip';
import { HOVER_TOOLTIP_ID } from 'src/constants';
import { InputSimple } from '../InputSimple';
import { HoverTooltip } from '../HoverTooltip';
import styles from './EditableText.module.scss';

interface EditableTextProps {
  initialText: string;
  textClassName?: string;
  className?: string;
  tooltipContent?: string;
  tooltipPlace?: PlacesType;
  onBlur: (newTitle: string) => void;
  onEditChange?: (isEditing: boolean) => void;
}

export const EditableText = ({
  initialText,
  className,
  textClassName,
  tooltipContent,
  tooltipPlace,
  onBlur,
  onEditChange,
}: EditableTextProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [text, setText] = useState(initialText);
  const inputRef = useRef<HTMLInputElement>(null);

  const tooltipId = `${HOVER_TOOLTIP_ID}-${useId()}`;

  useEffect(() => {
    setText(initialText);
  }, [initialText]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
      inputRef.current.classList.add('active');
    }
  }, [isEditing]);

  const handleChangeEditing = (editing: boolean) => {
    setIsEditing(editing);
    onEditChange?.(editing);
  };

  const handleBlur = () => {
    handleChangeEditing(false);
    const trimmed = text.trim();

    if (trimmed && trimmed !== initialText) {
      onBlur(trimmed);
    } else {
      setText(initialText);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      inputRef.current?.blur();
    }

    if (e.key === 'Escape') {
      setText(initialText);
      handleChangeEditing(false);
    }
  };

  return (
    <>
      {tooltipContent && <HoverTooltip id={tooltipId} />}

      <div
        className={classNames(styles.root, className)}
        onClick={() => handleChangeEditing(true)}
      >
        {isEditing ? (
          <InputSimple
            nodeRef={inputRef}
            value={text}
            onChange={setText}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            inputClassName={styles.input}
            inputWrapperClassName={styles.inputWrapper}
          />
        ) : (
          <p
            className={classNames(styles.text, textClassName)}
            data-tooltip-id={
              !isEditing && tooltipContent ? tooltipId : undefined
            }
            data-tooltip-content={tooltipContent}
            data-tooltip-place={tooltipPlace}
          >
            {text}
          </p>
        )}
      </div>
    </>
  );
};
